import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import BulkBuyPanel from "../components/BulkBuyPanel";
import { api, fmtMoney, convert } from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";

export default function Marketplace() {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const [projects, setProjects] = useState([]);
  const [typeFilter, setTypeFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [bulk, setBulk] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/projects").then((r) => setProjects(r.data || [])).finally(() => setLoading(false));
  }, []);

  const types = useMemo(() => ["All", ...Array.from(new Set(projects.map((p) => p.type)))], [projects]);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      if (typeFilter !== "All" && p.type !== typeFilter) return false;
      if (search && !`${p.name} ${p.region} ${p.certifier}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [projects, typeFilter, search]);

  return (
    <Layout>
      <SEO
        title="Marketplace — BMV-audited UCS Carbon Projects"
        description="Browse 7 BMV-audited UCS carbon credit projects across the Amazon, Cerrado and Atlantic Forest. Real-time UCS Index pricing, multi-currency settlement (USD/EUR/BRL/GBP/SAR)."
        path="/marketplace"
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1592288801240-7b18bb06e94f?w=2400&q=80" alt="mangrove" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/75 to-bg/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16">
          <div className="label-tag mb-4">{t("market.tag")}</div>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">{t("pages.marketplace")}</h1>
          <p className="text-ink2 mt-3 max-w-2xl">{t("market.sub")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
        <div className="border border-line bg-surface p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="inline-flex border border-line text-[11px] font-mono uppercase">
            <button
              type="button"
              onClick={() => setBulk(false)}
              data-testid="mode-single"
              className={`px-3 py-2 ${!bulk ? "bg-ink text-white" : "text-ink2 hover:text-ink"}`}
            >
              {t("market.mode_single")}
            </button>
            <button
              type="button"
              onClick={() => setBulk(true)}
              data-testid="mode-bulk"
              className={`px-3 py-2 border-l border-line ${bulk ? "bg-ink text-white" : "text-ink2 hover:text-ink"}`}
            >
              {t("market.mode_bulk")}
            </button>
          </div>
          {!bulk && (
            <>
              <input
                data-testid="market-search"
                placeholder={t("market.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input-field !py-2 flex-1"
              />
              <div className="flex flex-wrap gap-2">
                {types.map((tp) => (
                  <button key={tp} data-testid={`filter-${tp}`} onClick={() => setTypeFilter(tp)}
                    className={`px-4 py-2 text-xs font-mono tracking-wider border ${typeFilter === tp ? "bg-ink text-white border-ink" : "border-line text-ink2 hover:border-ink hover:text-ink"} rounded-none transition-colors`}>
                    {tp === "All" ? t("market.all").toUpperCase() : t(`project_types.${tp}`, tp).toUpperCase()}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {bulk && <BulkBuyPanel />}

      {!bulk && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {(() => {
          if (loading) {
            return <div className="text-center py-20 text-ink2" data-testid="market-loading">{t("market.loading")}</div>;
          }
          if (filtered.length === 0) {
            return <div className="text-center py-20 text-ink2">{t("market.empty")}</div>;
          }
          return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="market-grid">
            {filtered.map((p) => (
              <Link to={`/project/${p.id}`} key={p.id} className="card-tech group overflow-hidden" data-testid={`project-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img
                    src={p.image_url || "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1200"}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="label-tag">{t(`project_types.${p.type}`, p.type)}</div>
                    <div className="text-[11px] font-mono text-ink2">{p.certifier}</div>
                  </div>
                  <div className="font-serif text-xl mt-2 leading-tight flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.source === "producer" && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-leaf/40 bg-leaf/10 text-leaf"
                        data-testid={`badge-producer-${p.id}`}
                        title="Listed by a UCS producer, approved by GreenBelt compliance."
                      >
                        Producer-listed
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-ink2 mt-1">{p.region} · {t("market.vintage")} {p.vintage}</div>
                  <div className="mt-5 pt-5 border-t border-line flex items-end justify-between">
                    <div>
                      <div className="label-tag">{t("market.price_per")}</div>
                      <div className="mono-num text-2xl text-ink mt-0.5">
                        {fmtMoney(convert(p.price_usd_per_ton, currency), currency)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="label-tag">{t("market.available")}</div>
                      <div className="mono-num text-sm text-ink2 mt-0.5">{p.tons_available.toLocaleString()} t</div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          );
        })()}
      </section>
      )}
    </Layout>
  );
}
